import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateNoteTemplate,
  useDeleteNoteTemplate,
  useGetProducts,
  useNoteTemplates,
  useUpdateNoteTemplate,
} from "@/hooks";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";

const DEFAULT_SENTINEL = "__default__";

export default function NoteTemplatesPage() {
  const templatesQuery = useNoteTemplates();
  const productsQuery = useGetProducts();
  const createMutation = useCreateNoteTemplate();
  const updateMutation = useUpdateNoteTemplate();
  const deleteMutation = useDeleteNoteTemplate();

  const templates = templatesQuery.data ?? [];
  const products = productsQuery.data ?? [];

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formProductId, setFormProductId] = useState(DEFAULT_SENTINEL);
  const [formContent, setFormContent] = useState("");

  const resetForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormProductId(DEFAULT_SENTINEL);
    setFormContent("");
  };

  const defaultTemplate = templates.find((t) => !t.productId);

  const startAdd = () => {
    const firstUnused = products.find(
      (p) => !templates.some((t) => t.productId === p.id),
    );
    setFormProductId(firstUnused?.id ?? DEFAULT_SENTINEL);
    setFormContent("");
    setEditingId(null);
    setIsAdding(true);
  };

  const startEdit = (template: (typeof templates)[number]) => {
    setEditingId(template.id);
    setFormProductId(template.productId ?? DEFAULT_SENTINEL);
    setFormContent(template.content);
    setIsAdding(false);
  };

  const handleSave = async () => {
    const isDefault = formProductId === DEFAULT_SENTINEL;
    const selectedProduct = products.find((p) => p.id === formProductId);
    const data = {
      productId: isDefault ? undefined : formProductId,
      productName: isDefault ? undefined : selectedProduct?.name,
      content: formContent.trim(),
    };

    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, data });
    } else {
      await createMutation.mutateAsync(data);
    }
    resetForm();
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const canSave = formContent.trim().length > 0;

  const sorted = [
    ...templates.filter((t) => !t.productId), // default first
    ...templates.filter((t) => !!t.productId).sort((a, b) =>
      (a.productName ?? "").localeCompare(b.productName ?? ""),
    ),
  ];

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Note Templates</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pre-fill sell order notes by product. The default template is used when no product-specific one exists.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle>Templates</CardTitle>
            <CardDescription>
              Product-specific templates override the default.
            </CardDescription>
          </div>
          {!isAdding && !editingId && (
            <Button size="sm" onClick={startAdd}>
              <Plus className="h-4 w-4 mr-1" />
              Add Template
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-48 pl-6">Product</TableHead>
                <TableHead>Note Content</TableHead>
                <TableHead className="w-24 pr-6 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(isAdding || editingId) && (
                <TableRow className="bg-muted/30">
                  <TableCell className="pl-6 align-top pt-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Product</Label>
                      <Select value={formProductId} onValueChange={setFormProductId}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(!defaultTemplate || editingId === defaultTemplate.id) && (
                            <SelectItem value={DEFAULT_SENTINEL}>Default</SelectItem>
                          )}
                          {products
                            .filter((p) => {
                              const existingTemplate = templates.find((t) => t.productId === p.id);
                              return !existingTemplate || existingTemplate.id === editingId;
                            })
                            .map((product) => (
                              <SelectItem key={product.id} value={product.id}>
                                {product.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  <TableCell className="align-top pt-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Note Content</Label>
                      <Textarea
                        value={formContent}
                        onChange={(e) => setFormContent(e.target.value)}
                        placeholder="Enter template content..."
                        rows={3}
                        autoFocus
                      />
                    </div>
                  </TableCell>
                  <TableCell className="pr-6 align-top pt-4">
                    <div className="flex flex-col gap-1 items-end mt-5">
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={!canSave}
                        loading={isSaving}
                        loadingText={editingId ? "Saving..." : "Creating..."}
                      >
                        {editingId ? "Save" : "Create"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={resetForm}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {templatesQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={3} className="pl-6 text-muted-foreground text-sm py-6">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="pl-6 text-muted-foreground text-sm py-6">
                    No templates yet. Add one to start pre-filling sell order notes.
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((template) => {
                  const isEditing = editingId === template.id;
                  if (isEditing) return null;
                  return (
                    <TableRow key={template.id}>
                      <TableCell className="pl-6 font-medium align-top">
                        {template.productId
                          ? template.productName || template.productId.slice(0, 8)
                          : <span className="text-muted-foreground italic">Default</span>}
                      </TableCell>
                      <TableCell className="align-top">
                        <p className="text-sm text-muted-foreground line-clamp-2 whitespace-pre-wrap">
                          {template.content}
                        </p>
                      </TableCell>
                      <TableCell className="pr-6 align-top">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(template)}
                            disabled={isAdding || !!editingId}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteMutation.mutate(template.id)}
                            disabled={deleteMutation.isPending || isAdding || !!editingId}
                            loading={
                              deleteMutation.isPending &&
                              deleteMutation.variables === template.id
                            }
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
